import { useEffect, useState, useRef } from "react";
import { getSessionTables } from "@/Services/EventMeetingServices";

/**
 * Polls: GET /sessions/:sessionId/tables
 * Uses axiosInstance internally via getSessionTables (handles auth automatically)
 */
export function useSessionTablesPolling({
    sessionId,
    pollMs = 5000,
}) {
    const [tables, setTables] = useState([]);
    const inFlightRef = useRef(false);
    const cancelledRef = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setTables([]);
            return;
        }

        cancelledRef.current = false;
        inFlightRef.current = false;

        const fetchTablesNow = async () => {
            if (inFlightRef.current) return;
            inFlightRef.current = true;

            try {
                const res = await getSessionTables(sessionId);

                if (cancelledRef.current) return;

                // console.log("[useSessionTablesPolling] raw res:", res);

                // Normalize all possible API response shapes
                const arr =
                    Array.isArray(res) ? res              // res = [...]
                        : Array.isArray(res?.tables) ? res.tables       // res = { tables: [...] }
                            : Array.isArray(res?.data) ? res.data         // res = { data: [...] }
                                : Array.isArray(res?.data?.tables) ? res.data.tables  // res = { data: { tables: [...] } }
                                    : Array.isArray(res?.data?.data) ? res.data.data    // res = { data: { data: [...] } }
                                        : [];

                setTables(arr);
            } catch (e) {
                if (!cancelledRef.current) {
                    console.error("[useSessionTablesPolling] Failed to fetch tables:", e);
                }
            } finally {
                inFlightRef.current = false;
            }
        };

        fetchTablesNow();
        const interval = setInterval(fetchTablesNow, pollMs);

        return () => {
            cancelledRef.current = true;
            clearInterval(interval);
        };
    }, [sessionId, pollMs]);

    return { tables, setTables };
}